/**
 * Guided journeys (pack-journeys). The user answers questions; the engine builds records and
 * links on a draft graph and never shows them. Known facts become confirm steps. Each phase
 * closes on its gate — the decision tables named in the journey — and is saved to the kernel.
 * "Indietro" reopens earlier answers (pre-filled) and the half-done session is saved as a draft
 * after every answer (pack-journeys 0.5), so it can be resumed later or from another browser.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Checkbox, Col, Empty, Input, InputNumber, Popconfirm, Progress, Radio, Result, Row, Select, Space, Spin, Steps, Table, Tag, TreeSelect, Typography, message } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloudUploadOutlined, LogoutOutlined, RollbackOutlined, CompassOutlined, CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SafetyCertificateOutlined, SaveOutlined, TeamOutlined } from "@ant-design/icons";
import { TONE_FRAME_CLASS, TONE_ICON_CLASS } from "@evo-flywheel/ui";
import { kernel, type Condition, type Gate, type JourneyInfo, type PhaseProgress, type Prompt, type SavedDraft, type SegmentPreview, type Session } from "../api";
import { bindTarget, catalogOption, fieldOf, journeyDefs, loadNodes, nodeLabel, picklistOf, segmentPaths, stepOf, typesOf, type GraphNode, type JourneyDef, type StepDef } from "../journeys";
import { FindingsTable } from "../findings";

const FRAMEWORK: Record<string, string> = { gdpr: "GDPR", nis2: "NIS2", dlgs81: "D.Lgs. 81/08", aml: "Antiriciclaggio", aiact: "AI Act" };
const storeKey = (id: string) => `cmp-journey:v2:${id}`;

export const JourneyList: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<JourneyInfo[]>();
  const [drafts, setDrafts] = useState<Record<string, string | undefined>>({});
  useEffect(() => {
    kernel<JourneyInfo[]>("/journeys").then((js) => {
      setList(js);
      Promise.all(js.map((j) => kernel<SavedDraft>(`/journeys/${j.journey}/draft`).then((d) => [j.journey, d.session ? d.savedAt : undefined] as const).catch(() => [j.journey, undefined] as const)))
        .then((r) => setDrafts(Object.fromEntries(r)));
    }).catch((e) => message.error(e.message));
  }, []);
  if (!list) return <Spin />;
  return <div>
    <Typography.Title level={3}><span className={TONE_ICON_CLASS}><CompassOutlined /></span> Percorsi guidati</Typography.Title>
    <Typography.Paragraph type="secondary">
      Rispondi a domande semplici: il sistema compila registri, collegamenti e verifiche al posto tuo.
      Quello che è già presente nei dati acquisiti (o in un altro percorso) ti viene solo chiesto di confermarlo.
    </Typography.Paragraph>
    <Row gutter={[16, 16]}>
      {list.map((j) => {
        const saved = drafts[j.journey] ?? sessionStorage.getItem(storeKey(j.journey));
        return <Col key={j.journey} xs={24} md={12} xl={8}>
          <Card className={TONE_FRAME_CLASS} title={j.title} extra={j.frameworks.map((f) => <Tag key={f} color="geekblue">{FRAMEWORK[f] ?? f}</Tag>)}
            actions={[<Button type="primary" icon={<ArrowRightOutlined />} onClick={() => navigate(`/wizard/${j.journey}`)}>{saved ? "Riprendi" : "Inizia"}</Button>]}>
            <Typography.Text type="secondary">{j.phases.length} fasi</Typography.Text>
            {drafts[j.journey] && <div style={{ marginTop: 8 }}><Tag color="blue" icon={<CloudUploadOutlined />}>bozza salvata il {savedAtText(drafts[j.journey]!)}</Tag></div>}
            {j.reuses.length > 0 && <div style={{ marginTop: 8 }}><Tag color="green">riusa: {j.reuses.join(", ")}</Tag></div>}
          </Card>
        </Col>;
      })}
    </Row>
  </div>;
};

const savedAtText = (iso: string) => new Date(iso).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

type Stage = { kind: "prompt" } | { kind: "gate"; phase: string; gate?: Gate; committed?: CommitResult };
interface CommitResult { written: { entities: number; edges: number; events?: number }; corebos: Array<{ op: string; type: string; id: string; fields: Record<string, unknown> }>; deferredEdges: unknown[] }

export const JourneyRunner: React.FC = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  // A module launcher (ADR-033 slot action) passes the record and phase it was opened from:
  // that record's questions come first, then the phase's, then the rest of the journey.
  const [params] = useSearchParams();
  const focusSubject = params.get("subject"), focusPhase = params.get("phase");
  const def: JourneyDef | undefined = journeyDefs[id];
  const [session, setSession] = useState<Session>();
  const [all, setAll] = useState<Prompt[]>([]);
  const [stage, setStage] = useState<Stage>({ kind: "prompt" });
  const [busy, setBusy] = useState(false);
  const [coverage, setCoverage] = useState<PhaseProgress[]>([]);
  // "Indietro": the answered prompt being reviewed (null = the next open question)
  const [revisit, setRevisit] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string>();
  const [saveError, setSaveError] = useState(false);

  /** Server draft (pack-journeys 0.5): one per user and journey, written after every answer. */
  const saveDraft = async (s: Session, quiet = true) => {
    try {
      const r = await kernel<{ savedAt: string }>(`/journeys/${id}/draft`, { session: s }, "PUT");
      setSavedAt(r.savedAt); setSaveError(false);
      if (!quiet) message.success("Bozza salvata: puoi riprendere da qui quando vuoi");
      return true;
    } catch (e) {
      setSaveError(true);
      if (!quiet) message.error(`Bozza non salvata: ${(e as Error).message}`);
      return false;
    }
  };

  const refresh = async (s: Session, save = false): Promise<Prompt[]> => {
    sessionStorage.setItem(storeKey(id), JSON.stringify(s));
    setSession(s);
    const [ps, pr] = await Promise.all([
      kernel<Prompt[]>(`/journeys/${id}/prompts`, { session: s }),
      kernel<PhaseProgress[]>(`/journeys/${id}/progress`, { session: s }).catch(() => []),
    ]);
    setAll(ps);
    setCoverage(pr);
    if (save) void saveDraft(s);
    return ps;
  };

  const start = async () => {
    setBusy(true);
    try { const r = await kernel<{ session: Session }>(`/journeys/${id}/start`, {}); await refresh(r.session); setStage({ kind: "prompt" }); setRevisit(null); }
    catch (e) { message.error((e as Error).message); } finally { setBusy(false); }
  };

  const restart = async () => {
    sessionStorage.removeItem(storeKey(id));
    await kernel(`/journeys/${id}/draft`, undefined, "DELETE").catch(() => undefined);
    setSavedAt(undefined);
    await start();
  };

  useEffect(() => {
    // The saved draft wins (it follows the user across browsers); this tab's copy is the fallback.
    (async () => {
      const server = await kernel<SavedDraft>(`/journeys/${id}/draft`).catch(() => ({ session: null } as SavedDraft));
      const local = sessionStorage.getItem(storeKey(id));
      const s = server.session ?? (local ? JSON.parse(local) as Session : null);
      if (server.session) setSavedAt(server.savedAt);
      if (s) await refresh(s).catch(() => start()); else await start();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const open = all.filter((p) => !p.answered);
  const byKey = new Map(all.map((p) => [p.key, p]));
  // answered prompts in the order they were answered (prompts a later answer hid are left out)
  const history = (session?.answered ?? []).filter((k) => byKey.get(k)?.answered);
  const reviewing = revisit ? byKey.get(revisit) : undefined;
  const current = reviewing
    || (focusSubject && open.find((p) => p.item?.id === focusSubject))
    || (focusPhase && open.find((p) => p.phase === focusPhase))
    || open[0];
  const backTarget = reviewing ? history[history.indexOf(reviewing.key) - 1] : history[history.length - 1];
  const goBack = () => { if (backTarget) { setRevisit(backTarget); setStage({ kind: "prompt" }); } };
  // Candidates and labels: kernel records of the journey's types + this session's draft.
  const [known, setKnown] = useState<GraphNode[]>([]);
  const reloadKnown = () => { if (def) loadNodes(typesOf(def)).then(setKnown); };
  useEffect(reloadKnown, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const graph = useMemo(() => {
    const draft = session?.draft.created ?? [];
    const ids = new Set(draft.map((n) => n.id));
    return [...known.filter((n) => !ids.has(n.id)), ...draft];
  }, [known, session]);

  const phaseDone = (key: string) => all.filter((p) => p.phase === key).every((p) => p.answered);
  const phaseIndex = def ? def.phases.findIndex((p) => p.key === (stage.kind === "gate" ? stage.phase : current?.phase)) : 0;

  const runGate = async (phase: string) => {
    if (!session) return;
    setBusy(true);
    try { setStage({ kind: "gate", phase, gate: await kernel<Gate>(`/journeys/${id}/gate`, { session, phase }) }); }
    catch (e) { message.error((e as Error).message); } finally { setBusy(false); }
  };

  const submit = async (prompt: Prompt, answer: unknown) => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await kernel<{ session: Session; next: Prompt | null }>(`/journeys/${id}/answer`, { session, key: prompt.key, answer });
      const ps = await refresh(r.session, true);
      if (revisit) {
        // Reviewing: "Avanti" walks forward through the answers already given, then back to the open question.
        const answeredNow = new Set(ps.filter((p) => p.answered).map((p) => p.key));
        const hist = r.session.answered.filter((k) => answeredNow.has(k));
        setRevisit(hist[hist.indexOf(prompt.key) + 1] ?? null);
        return;
      }
      // Leaving a phase: show its gate before moving on.
      if (!r.next || r.next.phase !== prompt.phase) await gateFor(prompt.phase, r.session);
    } catch (e) { message.error((e as Error).message); } finally { setBusy(false); }
  };
  const gateFor = async (phase: string, s: Session) => {
    const gate = def?.phases.find((p) => p.key === phase)?.gate ? await kernel<Gate>(`/journeys/${id}/gate`, { session: s, phase }) : undefined;
    setStage({ kind: "gate", phase, gate });
  };

  // Bulk-confirm what is already known in a phase: one engine call per prompt (each answer may
  // reveal or hide later prompts), then a single refresh.
  const confirmKnown = async (phase: string) => {
    if (!session) return;
    setBusy(true);
    let s = session, n = 0;
    try {
      for (;;) {
        const ps = await kernel<Prompt[]>(`/journeys/${id}/prompts`, { session: s, phase });
        const p = ps.find((x) => !x.answered && x.mode === "confirm");
        if (!p) break;
        s = (await kernel<{ session: Session }>(`/journeys/${id}/answer`, { session: s, key: p.key, answer: { confirm: true } })).session;
        n++;
      }
      await refresh(s, true);
      message.success(`${n} risposte già note confermate`);
    } catch (e) { message.error((e as Error).message); await refresh(s, true); } finally { setBusy(false); }
  };

  const commit = async (phase: string) => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await kernel<CommitResult>(`/journeys/${id}/commit`, { session, phase });
      setStage((s) => s.kind === "gate" ? { ...s, committed: r } : s);
      reloadKnown();
      message.success(`Fase salvata: ${r.written.entities} schede, ${r.written.edges} collegamenti${r.written.events ? ", evento registrato" : ""}`);
    } catch (e) {
      const err = e as Error & { status?: number; data?: { gate?: Gate } };
      if (err.status === 409 && err.data?.gate) setStage({ kind: "gate", phase, gate: err.data.gate });
      message.error(err.status === 409 ? "Ci sono obblighi bloccanti da sanare prima di salvare" : err.message);
    } finally { setBusy(false); }
  };

  if (!def) return <Result status="404" title="Percorso sconosciuto" extra={<Button onClick={() => navigate("/wizard")}>Torna ai percorsi</Button>} />;
  if (!session) return <Spin />;

  const answered = all.filter((p) => p.answered).length;
  return <Row gutter={24}>
    <Col xs={24} lg={7}>
      <Card className={TONE_FRAME_CLASS} size="small" title={def.title}>
        <Progress percent={all.length ? Math.round((answered / all.length) * 100) : 0} size="small" />
        <Steps direction="vertical" size="small" current={phaseIndex} style={{ marginTop: 12 }}
          items={def.phases.map((p) => ({
            title: p.title,
            status: phaseDone(p.key) ? "finish" : p.key === current?.phase ? "process" : "wait",
            description: <Space size={4} wrap>
              <Typography.Text type="secondary">{all.filter((x) => x.phase === p.key && x.answered).length}/{all.filter((x) => x.phase === p.key).length}</Typography.Text>
              {all.some((x) => x.phase === p.key && !x.answered && x.mode === "confirm") &&
                <Button size="small" type="link" icon={<CheckOutlined />} onClick={() => confirmKnown(p.key)}>conferma noti ({all.filter((x) => x.phase === p.key && !x.answered && x.mode === "confirm").length})</Button>}
              {p.gate && <Button size="small" type="link" icon={<SafetyCertificateOutlined />} onClick={() => runGate(p.key)}>verifica</Button>}
              <CoverageMeter progress={coverage.find((c) => c.phase === p.key)} />
            </Space>,
          }))} />
        <Popconfirm title="Ricominciare da capo?" description="Le risposte non ancora salvate nelle fasi andranno perse." okText="Ricomincia" cancelText="Annulla" onConfirm={restart}>
          <Button block danger type="text">Ricomincia</Button>
        </Popconfirm>
      </Card>
    </Col>
    <Col xs={24} lg={17}>
      <Spin spinning={busy}>
        <Space style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }} wrap>
          <Space wrap>
            <Button icon={<ArrowLeftOutlined />} disabled={!backTarget || busy} onClick={goBack}>Indietro</Button>
            {reviewing && <Button type="link" icon={<RollbackOutlined />} onClick={() => setRevisit(null)}>Torna alla domanda da compilare</Button>}
          </Space>
          <Space wrap>
            <Typography.Text type={saveError ? "danger" : "secondary"}>
              {saveError ? "Bozza non salvata" : savedAt ? `Bozza salvata alle ${new Date(savedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : ""}
            </Typography.Text>
            <Button icon={<CloudUploadOutlined />} disabled={busy} onClick={() => saveDraft(session, false)}>Salva bozza</Button>
            <Button icon={<LogoutOutlined />} disabled={busy} onClick={async () => { if (await saveDraft(session, false)) navigate("/wizard"); }}>Salva ed esci</Button>
          </Space>
        </Space>
        {reviewing && stage.kind === "prompt" && <Alert style={{ marginBottom: 12 }} type="info" showIcon
          message="Stai rivedendo una risposta già data" description="Modificala e premi Avanti, oppure torna indietro ancora. Nulla va nei registri finché non salvi la fase." />}
        {stage.kind === "gate"
          ? <GatePanel def={def} phase={stage.phase} gate={stage.gate} committed={stage.committed} labels={Object.fromEntries(graph.map((n) => [n.id, nodeLabel(n)]))} onCommit={() => commit(stage.phase)} onContinue={() => setStage({ kind: "prompt" })} hasNext={!!current} />
          : current
            ? <PromptCard key={current.key} journey={id} session={session} prompt={current} step={stepOf(def, current.phase, current.step)!} phaseTitle={def.phases.find((p) => p.key === current.phase)!.title} graph={graph} onAnswer={(a) => submit(current, a)} />
            : <Result status="success" title="Percorso completato" subTitle="Tutte le risposte sono state raccolte. Verifica e salva le fasi dal pannello a sinistra."
                extra={<Button onClick={() => navigate("/regole")}>Vai alle verifiche</Button>} />}
      </Spin>
    </Col>
  </Row>;
};

const GatePanel: React.FC<{ def: JourneyDef; phase: string; gate?: Gate; committed?: CommitResult; labels: Record<string, string>; onCommit: () => void; onContinue: () => void; hasNext: boolean }> =
  ({ def, phase, gate, committed, labels, onCommit, onContinue, hasNext }) => {
  const title = def.phases.find((p) => p.key === phase)?.title;
  const unmet = gate?.findings.filter((f) => f.status === "unmet") ?? [];
  return <Card className={TONE_FRAME_CLASS} title={<Space><SafetyCertificateOutlined />Verifica fase: {title}</Space>}>
    {!gate ? <Alert type="info" showIcon message="Questa fase non ha verifiche normative: puoi salvarla." />
      : gate.passed
        ? <Alert type="success" showIcon message="Nessun obbligo bloccante aperto" description={unmet.length ? `${unmet.length} avvisi da considerare.` : `Regole applicate: ${gate.tables.join(", ")}`} />
        : <Alert type="error" showIcon message={`${gate.blocking.length} obblighi bloccanti`} description="Completa le risposte indicate prima di salvare la fase." />}
    {gate && gate.findings.length > 0 && <div style={{ marginTop: 16 }}><FindingsTable findings={gate.findings} labels={labels} compact /></div>}
    {committed && <Alert style={{ marginTop: 16 }} type="success" showIcon
      message={`Salvati ${committed.written.entities} schede e ${committed.written.edges} collegamenti nel registro`}
      description={committed.corebos.length > 0 && <>
        <Typography.Paragraph style={{ marginBottom: 4 }}>{committed.corebos.length} schede da registrare nei dati acquisiti (in attesa di invio):</Typography.Paragraph>
        <Table size="small" pagination={false} rowKey="id" dataSource={committed.corebos}
          columns={[{ title: "Operazione", dataIndex: "op" }, { title: "Tipo", dataIndex: "type" }, { title: "Dati", render: (_, r) => <code>{JSON.stringify(r.fields)}</code> }]} />
      </>} />}
    <Space style={{ marginTop: 16 }}>
      <Button type="primary" icon={<SaveOutlined />} disabled={!!committed || (gate ? !gate.passed : false)} onClick={onCommit}>Salva fase</Button>
      {hasNext && <Button icon={<ArrowRightOutlined />} onClick={onContinue}>Continua</Button>}
    </Space>
  </Card>;
};

/** W5: share of the phase's gate obligations already met. */
const CoverageMeter: React.FC<{ progress?: PhaseProgress }> = ({ progress }) => {
  const g = progress?.gate;
  if (!g || g.met + g.unmet === 0) return null;
  const pct = Math.round((g.met / (g.met + g.unmet)) * 100);
  return <Progress style={{ width: 120, margin: 0 }} size="small" percent={pct} status={g.blocking ? "exception" : pct === 100 ? "success" : "normal"}
    format={() => `${g.met}/${g.met + g.unmet}`} />;
};

const PromptCard: React.FC<{ journey: string; session: Session; prompt: Prompt; step: StepDef; phaseTitle: string; graph: GraphNode[]; onAnswer: (a: unknown) => void }> =
  ({ journey, session, prompt, step, phaseTitle, graph, onAnswer }) => {
  const [editing, setEditing] = useState(prompt.mode !== "confirm");
  const labelOf = (id: string) => { const n = graph.find((x) => x.id === id); return n ? nodeLabel(n) : id; };
  const knownText = Array.isArray(prompt.known) ? (prompt.known as string[]).map(labelOf).join(", ") : String(prompt.known ?? "");
  return <Card className={TONE_FRAME_CLASS} title={<Typography.Text type="secondary">{phaseTitle}</Typography.Text>}>
    <Typography.Title level={4} style={{ marginTop: 0 }}>{prompt.ask}</Typography.Title>
    {prompt.known !== undefined && <Alert style={{ marginBottom: 16 }} type="info" showIcon
      message={prompt.knownFrom === "base" ? "Già noto (dai dati acquisiti o da un altro percorso)" : "Già indicato in questo percorso"}
      description={knownText} />}
    {prompt.copyFrom && prompt.copyFrom.length > 0 && editing && <Space style={{ marginBottom: 16 }}>
      <CopyOutlined /><Typography.Text type="secondary">Come per</Typography.Text>
      <Select style={{ minWidth: 240 }} placeholder="scegli un elemento già compilato" options={prompt.copyFrom.map((c) => ({ value: c.id, label: c.label }))}
        onChange={(from: string) => onAnswer({ copy_from: from })} />
    </Space>}
    {!editing
      ? <Space><Button type="primary" icon={<CheckOutlined />} onClick={() => onAnswer({ confirm: true })}>Confermo</Button>
          <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>Modifica</Button></Space>
      : step.mode === "matrix"
        ? <MatrixAnswer step={step} prompt={prompt} graph={graph} onAnswer={onAnswer} />
        : step.mode === "segment"
          ? <SegmentAnswer journey={journey} session={session} step={step} prompt={prompt} onAnswer={onAnswer} />
          : <Answer step={step} prompt={prompt} graph={graph} onAnswer={onAnswer} />}
  </Card>;
};

/** W2/W3: rows x the candidates each row may tick; a ticked catalogue cell carries a status. */
const MatrixAnswer: React.FC<{ step: StepDef; prompt: Prompt; graph: GraphNode[]; onAnswer: (a: unknown) => void }> = ({ step, prompt, graph, onAnswer }) => {
  const statusField = step.creates ? Object.keys(fieldOf(`${step.creates}.implementation_status`) ? { implementation_status: 1 } : {})[0] : undefined;
  const statuses = statusField ? picklistOf(`${step.creates}.${statusField}`) ?? [] : [];
  const codeField = Object.keys(step.dedupe_on ?? {})[0];
  const label = new Map((prompt.candidates ?? []).map((c) => [c.id, c.label]));
  // pre-tick what is already recorded for each row (records in scope, matched by the dedupe field)
  const initial = useMemo(() => Object.fromEntries((prompt.rows ?? []).map((r) => {
    const ids = Array.isArray(r.known) ? (r.known as string[]) : [];
    if (step.links) return [r.id, Object.fromEntries(ids.map((x) => [x, {}]))];
    return [r.id, Object.fromEntries(ids.map((x) => graph.find((n) => n.id === x)).filter(Boolean)
      .map((n) => [String(n!.fields[codeField] ?? n!.id), statusField ? { [statusField]: n!.fields[statusField] } : {}]))];
  })), [prompt, graph]); // eslint-disable-line react-hooks/exhaustive-deps
  const [cells, setCells] = useState<Record<string, Record<string, Record<string, unknown>>>>(initial);
  const toggle = (row: string, id: string, on: boolean) => setCells((c) => {
    const r = { ...(c[row] ?? {}) };
    if (on) r[id] = statusField ? { [statusField]: statuses[0] } : {}; else delete r[id];
    return { ...c, [row]: r };
  });
  const setStatus = (row: string, id: string, v: string) => setCells((c) => ({ ...c, [row]: { ...c[row], [id]: { [statusField!]: v } } }));
  const all = (prompt.candidates ?? []).map((c) => c.id);
  return <>
    {(prompt.rows ?? []).map((r) => {
      const allowed = r.allowed ?? all;
      const rowNode = graph.find((n) => n.id === r.id);
      return <Card key={r.id} size="small" style={{ marginBottom: 12 }} title={rowNode ? nodeLabel(rowNode) : r.label}
        extra={<Typography.Text type="secondary">{Object.keys(cells[r.id] ?? {}).length}/{allowed.length}</Typography.Text>}>
        {allowed.length === 0 && <Typography.Text type="secondary">Nessuna voce del catalogo si applica.</Typography.Text>}
        {allowed.map((id) => {
          const on = !!cells[r.id]?.[id];
          return <Row key={id} align="middle" gutter={8} style={{ padding: "2px 0" }}>
            <Col flex="auto"><Checkbox checked={on} onChange={(e) => toggle(r.id, id, e.target.checked)}><code>{id}</code> {label.get(id) ?? id}</Checkbox></Col>
            {statusField && on && <Col><Select size="small" style={{ width: 160 }} value={cells[r.id][id][statusField] as string}
              options={statuses.map((v) => ({ value: v, label: v.replace(/_/g, " ") }))} onChange={(v: string) => setStatus(r.id, id, v)} /></Col>}
          </Row>;
        })}
      </Card>;
    })}
    <Button type="primary" icon={<ArrowRightOutlined />} onClick={() => onAnswer({ matrix: step.links ? Object.fromEntries(Object.entries(cells).map(([k, v]) => [k, Object.keys(v)])) : cells })}>Avanti</Button>
  </>;
};

/** W6: recipients as a rule over people, previewed by the engine before anything is created. */
const SegmentAnswer: React.FC<{ journey: string; session: Session; step: StepDef; prompt: Prompt; onAnswer: (a: unknown) => void }> = ({ journey, session, step, prompt, onAnswer }) => {
  const of = step.segment!.of;
  const paths = useMemo(() => segmentPaths(of), [of]);
  const [conds, setConds] = useState<Array<{ path?: string; values: string[] }>>([{ values: [] }]);
  const [preview, setPreview] = useState<SegmentPreview>();
  const cellOf = (c: { path?: string; values: string[] }): Condition | undefined => {
    if (!c.path) return undefined;
    if (c.path.includes(".has.")) return { path: c.path, cell: true };
    if (!c.values.length) return undefined;
    return { path: c.path, cell: c.values.length === 1 && /^[\w.-]+$/.test(c.values[0]) ? c.values[0] : `in [${c.values.join(", ")}]` };
  };
  const rule = conds.map(cellOf).filter((c): c is Condition => !!c);
  useEffect(() => {
    if (!rule.length) { setPreview(undefined); return; }
    const t = setTimeout(() => kernel<SegmentPreview>(`/journeys/${journey}/segment-preview`, { session, key: prompt.key, segment: rule }).then(setPreview).catch((e) => message.error(e.message)), 300);
    return () => clearTimeout(t);
  }, [JSON.stringify(rule)]); // eslint-disable-line react-hooks/exhaustive-deps
  const fresh = preview ? preview.matching - preview.alreadyCovered : 0;
  return <>
    <Typography.Paragraph type="secondary">Tutte le condizioni devono valere. Chi ha già una scheda per questo documento non ne riceve un'altra.</Typography.Paragraph>
    {conds.map((c, i) => <Space key={i} style={{ display: "flex", marginBottom: 8 }} align="start">
      <Select style={{ width: 280 }} placeholder="Condizione" options={paths} value={c.path} showSearch optionFilterProp="label"
        onChange={(path: string) => setConds((xs) => xs.map((x, k) => (k === i ? { path, values: [] } : x)))} />
      {c.path && !c.path.includes(".has.") && <Select style={{ width: 280 }} mode="tags" placeholder="valori (uno o più)" value={c.values}
        onChange={(values: string[]) => setConds((xs) => xs.map((x, k) => (k === i ? { ...x, values } : x)))} />}
      <Button icon={<DeleteOutlined />} onClick={() => setConds((xs) => xs.filter((_, k) => k !== i))} />
    </Space>)}
    <Button type="dashed" icon={<PlusOutlined />} onClick={() => setConds((xs) => [...xs, { values: [] }])}>Aggiungi condizione</Button>
    {preview && <Alert style={{ marginTop: 16 }} type={fresh ? "info" : "warning"} showIcon icon={<TeamOutlined />}
      message={`${preview.matching} ${preview.matching === 1 ? "persona corrisponde" : "persone corrispondono"}; ${fresh === 1 ? "1 riceverà" : `${fresh} riceveranno`} una richiesta di accettazione`}
      description={preview.sample.map((p) => p.label).join(", ") + (preview.matching > preview.sample.length ? "…" : "")} />}
    <Space style={{ marginTop: 16 }}>
      <Button type="primary" icon={<ArrowRightOutlined />} disabled={!rule.length}
        onClick={() => onAnswer({ segment: rule, fields: { acceptance_status: "Pending", ...(prompt.item ? { acctitle: prompt.item.label } : {}) } })}>Crea le richieste</Button>
      <Button type="link" onClick={() => onAnswer({ create: [] })}>Nessuno per ora</Button>
    </Space>
  </>;
};

const Answer: React.FC<{ step: StepDef; prompt: Prompt; graph: GraphNode[]; onAnswer: (a: unknown) => void }> = ({ step, prompt, graph, onAnswer }) => {
  // Revisited with "Indietro": start from the answer given before; otherwise from what is known.
  const prev = (prompt.previous ?? {}) as { value?: unknown; targets?: string[]; create?: Array<{ label?: string; catalog?: string }> };
  const [value, setValue] = useState<unknown>("value" in prev ? prev.value : prompt.known);
  const [tags, setTags] = useState<string[]>(() =>
    prev.targets || prev.create ? [...(prev.targets ?? []), ...(prev.create ?? []).map((c) => c.catalog ?? c.label ?? "").filter(Boolean)]
      : step.links && Array.isArray(prompt.known) ? (prompt.known as string[]) : []);
  const [text, setText] = useState((prev.create ?? []).map((c) => c.label).filter(Boolean).join("\n"));
  const catalog = (prompt.options ?? []).map(catalogOption);
  const nodesOf = (type?: string) => graph.filter((n) => n.type === type).map((n) => ({ value: n.id, label: nodeLabel(n) }));
  const skip = <Button type="link" onClick={() => onAnswer(step.links ? { targets: [] } : step.creates ? { create: [] } : { value: null })}>Non applicabile / salta</Button>;
  const go = (a: unknown) => <Space style={{ marginTop: 16 }}><Button type="primary" icon={<ArrowRightOutlined />} onClick={() => onAnswer(a)}>Avanti</Button>{skip}</Space>;

  // links: pick existing records of the other end (+ new ones in *-or-create modes)
  if (step.links) {
    // W1: the engine's filtered candidates when the step narrows them; every record of the type otherwise
    const opts = prompt.candidates ? prompt.candidates.map((c) => ({ value: c.id, label: c.label })) : nodesOf(step.links.to);
    const canCreate = step.mode.endsWith("-or-create");
    const multi = step.mode.startsWith("multi");
    const known = new Set(opts.map((o) => o.value));
    if (step.tree && prompt.candidates) {
      // W9: tree picker over the parent field
      const ids = new Set(prompt.candidates.map((c) => c.id));
      const tree = prompt.candidates.map((c) => ({ id: c.id, pId: c.parent && ids.has(c.parent) ? c.parent : 0, value: c.id, title: c.label }));
      return <>
        <TreeSelect style={{ width: "100%" }} treeDataSimpleMode treeData={tree} treeDefaultExpandAll multiple={multi} treeCheckable={multi}
          showCheckedStrategy={TreeSelect.SHOW_ALL} value={tags} onChange={(v: string[] | string) => setTags(Array.isArray(v) ? v : [v])} placeholder="Scegli" />
        {go({ targets: tags })}
      </>;
    }
    return <>
      <Select style={{ width: "100%" }} mode={canCreate ? "tags" : multi ? "multiple" : undefined} options={opts} value={tags}
        placeholder={canCreate ? "Scegli o scrivi e premi Invio" : "Scegli"} optionFilterProp="label"
        onChange={(v: string[] | string) => setTags(Array.isArray(v) ? v : [v])}
        notFoundContent={canCreate ? undefined : <Empty description="Nessun elemento ancora inserito" />} />
      {go({ targets: tags.filter((t) => known.has(t)), ...(canCreate ? { create: tags.filter((t) => !known.has(t)).map((label) => ({ label })) } : {}) })}
    </>;
  }
  // creates: list of new records, optionally from a catalogue
  if (step.creates) {
    if (catalog.length || step.mode.includes("pick")) {
      const codes = new Set(catalog.map((c) => c.value));
      return <>
        <Select style={{ width: "100%" }} mode="tags" options={catalog} value={tags} optionFilterProp="label" placeholder="Scegli dal catalogo o scrivi e premi Invio" onChange={setTags} />
        {go({ create: tags.map((t) => codes.has(t) ? { catalog: t } : { label: t }) })}
      </>;
    }
    return <>
      <Input.TextArea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="Uno per riga" />
      {go({ create: text.split("\n").map((l) => l.trim()).filter(Boolean).map((label) => ({ label })) })}
    </>;
  }
  // bind: a field value or a related record
  const bind = step.bind!;
  const target = bindTarget(bind);
  if (target) {
    return <><Select style={{ width: "100%" }} showSearch optionFilterProp="label" options={nodesOf(target)} value={value as string} onChange={setValue}
      placeholder="Scegli" notFoundContent={<Empty description="Nessuno ancora inserito" />} />{go({ value })}</>;
  }
  const field = fieldOf(bind);
  const list = catalog.length ? catalog : (picklistOf(bind) ?? []).map((v) => ({ value: v, label: v.replace(/_/g, " ") }));
  if (list.length) return <><Select style={{ width: "100%" }} showSearch optionFilterProp="label" options={list} value={value as string} onChange={setValue} placeholder="Scegli" />{go({ value })}</>;
  if (field?.type === "date") {
    // W8: the suggested date is shown pre-filled; it is applied only when the user goes on
    const v = (value as string) ?? (prompt.suggested as string) ?? "";
    return <><Input type="date" style={{ width: 200 }} value={v.slice(0, 10)} onChange={(e) => setValue(e.target.value)} />
      {prompt.suggested !== undefined && value === undefined && <Tag style={{ marginLeft: 8 }} color="blue">suggerita</Tag>}{go({ value: v || null })}</>;
  }
  if (field?.type === "boolean" || step.mode === "confirm-if-known")
    return <><Radio.Group value={value} onChange={(e) => setValue(e.target.value)}><Radio.Button value={true}>Sì</Radio.Button><Radio.Button value={false}>No</Radio.Button></Radio.Group>{go({ value })}</>;
  if (field?.type === "number" || field?.type === "decimal")
    return <><InputNumber style={{ width: 200 }} value={value as number} onChange={setValue} />{go({ value })}</>;
  return <><Input value={(value as string) ?? ""} onChange={(e) => setValue(e.target.value)} onPressEnter={() => onAnswer({ value })} />{go({ value })}</>;
};

